// noinspection JSUnusedGlobalSymbols

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { UnsubscribeOnDestroyAdapter } from '@shared';

import { environment } from '@enviroment/environment';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { UserDetails } from '@core/models/UserDetails';
import { MsgResponse } from '@core/models/MsgResponse';
import { CreateUserResponse } from '@core/models/Http/Response/CreateUserResponse';

import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { GeneralFunctionsService } from '@core/service/generalFunctions.service';

// ✅ Imports que venían en AdminService
import { ImageSignature } from '@core/models/ImageSignature';
import { UserType } from '@core/models/UserType';
import { UsuarioAssetUpsert } from '@core/models/UsuarioAsset';

type UsuarioAssetDto = {
  idAsset?: string;
  idUsuario?: string;
  tipo?: number;
  contentType?: string; // "image/jpeg"
  bytesSize?: number;
  widthPx?: number;
  heightPx?: number;
  base64?: string; // "/9j/4AAQ..."
  fechaCreacion?: string;
};

@Injectable({
  providedIn: 'root',
})
export class UserService extends UnsubscribeOnDestroyAdapter {
  /** ====== TABLA ====== */
  isTblLoading = true;
  dataChange: BehaviorSubject<UserDetails[]> = new BehaviorSubject<UserDetails[]>([]);

  /** ====== USERNAME EN LOCALSTORAGE ====== */
  private usuarioSubject = new BehaviorSubject<string | null>(
    localStorage.getItem('userName')
  );
  userName$ = this.usuarioSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private httpMsgHandler: HttpMesageHandlerService,
    private gfs: GeneralFunctionsService
  ) {
    super();
  }

  /** ====== USERNAME ====== */
  setUserName(nameSurname: string): void {
    localStorage.setItem('userName', nameSurname);
    this.usuarioSubject.next(nameSurname);
  }

  get data(): UserDetails[] {
    return this.dataChange.value;
  }

  /** GUID todo en ceros que a veces regresa el backend */
  private static ZERO_GUID_REGEX = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;
  private static isZeroGuid(v?: string | null): boolean {
    return typeof v === 'string' && UserService.ZERO_GUID_REGEX.test(v);
  }

  /** Limpia filas inválidas y compone nombre completo; coalesce a '' para evitar undefined */
  private mapAndClean(rows: UserDetails[] | null | undefined): UserDetails[] {
    const safe = Array.isArray(rows) ? rows : [];
    return safe
      .filter(u => u && !UserService.isZeroGuid(u.idUsuario))
      .map(u => {
        const nombres = `${u.nombres ?? ''} ${u.primerApellido ?? ''} ${u.segundoApellido ?? ''}`
          .replace(/\s+/g, ' ')
          .trim();

        return {
          ...u,
          nombres,
          domicilio: u.domicilio ?? '',
          municipio: u.municipio ?? '',
          nombreAsentamiento: u.nombreAsentamiento ?? '',
          nombreSucursal: u.nombreSucursal ?? '',
          email: u.email ?? '',
          codigoPostal: u.codigoPostal ?? '',
        } as UserDetails;
      });
  }

  // ==========================
  //        USUARIOS (LISTADOS)
  // ==========================
  /**
   * AdminService.getUsers() original:
   * hacía POST `${backend}Usuarios` con {}
   * Lo dejo igual para compatibilidad.
   */
  getUsers() {
    return this.httpClient.post<DefaultResponse<UserDetails[]>>(
      `${environment.backend_url}Usuarios`,
      {}
    );
  }

  /** Tabla por GEMP (query param) */
  getAllUsers(idGEMP?: string): void {
    if (!idGEMP) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    this.isTblLoading = true;

    const params = new HttpParams().set('IdGEMP', idGEMP);

    this.subs.sink = this.httpClient
      .get<DefaultResponse<UserDetails[]>>(
        `${environment.backend_url}Usuarios`,
        { params }
      )
      .pipe(finalize(() => (this.isTblLoading = false)))
      .subscribe({
        next: (res) => {
          const cleaned = this.mapAndClean(res.data);
          this.dataChange.next(cleaned);
        },
        error: (err: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(err);
          this.dataChange.next([]);
        },
      });
  }

  /** Tabla por Sucursal (query param) */
  getAllUsersByIdBranch(idBranch: string): void {
    this.isTblLoading = true;

    const params = new HttpParams().set('IdSucursal', idBranch);

    this.subs.sink = this.httpClient
      .get<DefaultResponse<UserDetails[]>>(
        `${environment.backend_url}Usuarios`,
        { params }
      )
      .pipe(finalize(() => (this.isTblLoading = false)))
      .subscribe({
        next: (res) => {
          const cleaned = this.mapAndClean(res.data);
          this.dataChange.next(cleaned);
        },
        error: (err: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(err);
          this.dataChange.next([]);
        },
      });
  }

  /** UPSERT */
  createUpdateUser(data: UserDetails) {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Usuarios/UpSert`;
    return this.httpClient.post<DefaultResponse<CreateUserResponse>>(url, cleanData);
  }

  /** Alias por compatibilidad (si en algún lugar usabas updateUser) */
  updateUser(data: UserDetails) {
    return this.createUpdateUser(data);
  }

  // ==========================
  //     TIPOS DE USUARIO
  // ==========================
  getTiposUsuario() {
    return this.httpClient.get<DefaultResponse<UserType[]>>(
      `${environment.backend_url}TipoUsuario`
    );
  }

  // ==========================
  //        CONFIRM EMAIL
  // ==========================

  /**
   * CONFIRMAR CUENTA / VERIFY EMAIL + CAMBIAR CONTRASEÑA
   * Endpoint (Swagger): /api/Usuarios/verify-email
   * Body: { token, newPassword, confirmPassword }
   */
  verifyEmail(token: string, newPassword: string, confirmPassword: string) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.httpClient.post<DefaultResponse<string>>(
      `${environment.backend_url}Usuarios/verify-email`,
      { token, newPassword, confirmPassword },
      { headers }
    );
  }

  resendVerifyEmail(email: string) {
    const cleanEmail = (email ?? '').trim().replace(/^"+|"+$/g, '');

    return this.httpClient.post<DefaultResponse<any>>(
      `${environment.backend_url}Usuarios/resend-verify-email`,
      JSON.stringify(cleanEmail),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        })
      }
    );
  }

  // ==========================
  //        IMÁGENES / ASSETS
  // ==========================

  /**
   * NUEVO método para subir assets (UpSert general)
   * Endpoint: api/UsuarioAssets/UpSert
   */
  uploadUsuarioAsset(data: UsuarioAssetUpsert) {
    const url = `${environment.backend_url}UsuarioAssets/UpSert`;
    return this.httpClient.post<DefaultResponse<string>>(url, data);
  }

  /**
   * Subir firma
   * Endpoint: api/UsuarioAssets/UpSertFirma
   */
  uploadUsuarioAssetFirma(data: UsuarioAssetUpsert) {
    const url = `${environment.backend_url}UsuarioAssets/UpSertFirma`;
    return this.httpClient.post<DefaultResponse<string>>(url, data);
  }

  /**
   * Metodo anterior de compatibilidad (base64)
   * Endpoint: Usuarios/imagen-firma
   */
  uploadImages(data: ImageSignature) {
    const url = `${environment.backend_url}Usuarios/imagen-firma`;
    return this.httpClient.post<DefaultResponse<string>>(url, data);
  }

  /** GET imagen (respuesta cruda DefaultResponse<any>) */
  getUsuarioImagen(idUsuario: string) {
    const url = `${environment.backend_url}UsuarioAssets/imagen/${idUsuario}`;
    return this.httpClient.get<DefaultResponse<any>>(url);
  }

  /** GET firma (respuesta cruda DefaultResponse<any>) */
  getUsuarioFirma(idUsuario: string) {
    const url = `${environment.backend_url}UsuarioAssets/firma/${idUsuario}`;
    return this.httpClient.get<DefaultResponse<any>>(url);
  }

  /**
   * GET asset por tipo (query)
   * Endpoint: UsuarioAssets/asset?tipo=1&idUsuario=...
   */
  getUsuarioAsset(idUsuario: string, tipo: number) {
    const url = `${environment.backend_url}UsuarioAssets/asset`;

    const params = new HttpParams()
      .set('tipo', String(tipo))
      .set('idUsuario', idUsuario);

    return this.httpClient.get<DefaultResponse<any>>(url, { params });
  }

  /**
   * Helper que ya tenías: devuelve string listo para <img [src]>
   * "data:image/jpeg;base64,..."
   */
  getUserImageBase64(idUsuario: string): Observable<string> {
    const url = `${environment.backend_url}UsuarioAssets/imagen/${idUsuario}`;

    return this.httpClient.get<any>(url).pipe(
      map((res) => {
        const payload: UsuarioAssetDto = (res?.data ?? res) as UsuarioAssetDto;

        const base64 = (payload?.base64 ?? '').trim();
        if (!base64) return '';

        const ct = (payload?.contentType ?? 'image/png').trim();

        if (base64.startsWith('data:image')) return base64;

        return `data:${ct};base64,${base64}`;
      })
    );
  }

  getUserByUsr(usr: string) {
    const params = new HttpParams().set('Usr', (usr ?? '').trim());

    return this.httpClient.get<DefaultResponse<UserDetails[]>>(
      `${environment.backend_url}Usuarios`,
      { params }
    );
  }

  // ==========================
  //          DELETE
  // ==========================

  deleteUser(id: string): void {
    this.httpClient
      .post(`${environment.backend_url}Usuarios/eliminar-usuario/${id}`, {})
      .subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res as MsgResponse);
        },
        error: (err: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(err);
        },
      });
  }
}
